import DatePicker from '@grnhse/seedling/lib/birch/components/forms/date_picker';
import React, {
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import get from 'lodash/get';
import styled from 'styled-components';
import TinyMCE from 'tinymce/localized_editor_with_ai';
import {
  Controller,
  RegisterOptions,
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  MultiDropdown,
  SingleDropdown,
  useMultiDropdown,
  useSingleDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { Grid } from '@grnhse/seedling/lib/azalea/layouts/grid';
import BirchTextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import BirchTextArea from '@grnhse/seedling/lib/birch/components/forms/text_area';
import {
  FormField,
  FormLabel,
} from '@grnhse/seedling/lib/azalea/components/forms/layout';
import AttachmentIcon from '@grnhse/seedling/lib/birch/icons/attachment';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import VideoCallIcon from '@grnhse/seedling/lib/birch/icons/video_call';
import GoogleMeetIcon from '../../../shared/images/meet.png';
import MSTeamsIcon from '../../../shared/images/teams.png';
import ZoomIcon from '../../../shared/images/zoom.png';
import {
  IconButton,
  PrimaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import {
  Body,
  Label,
  Link,
  SecondaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import InlineIcons from '@grnhse/seedling/lib/birch/typography/inline_icons';
import RichText from '@grnhse/seedling/lib/azalea/components/forms/rich_text';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';
import AvailabilityCalendar from './Calendar';
import type {
  CalendarEditMode,
  CalendarEvent,
  TemplateAttachment,
  UserDateFormat,
  UserTimeFormat,
  VideoConferencingProviderType,
} from '../types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  ExpandableMenu,
  MenuItem,
  MenuList,
  TransparentMenuButton,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';
import EllipsisIcon from '@grnhse/seedling/lib/birch/icons/ellipsis';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import TextArea from '@grnhse/seedling/lib/birch/components/forms/text_area';
import TinyMCEConfig from 'tinymce/config';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import type { UseFormGetValues } from 'react-hook-form/dist/types/form';
import type { FieldValues } from 'react-hook-form/dist/types/fields';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { AI_PRODUCT } from '../../../tinymce/lib/consts';
import { CalendarApi, DatesSetArg } from '@fullcalendar/core';

const t = withLocalePrefix('candidate_profile_redesign.modals.email');
const availability = withLocalePrefix('availability');

const RequiredAsterisk = styled.span`
  color: red;
`;

export type DropdownOption = {
  label: string;
  value: string;
  disabled?: boolean;
  items?: DropdownOption[];
  userCreated?: boolean;
  greenhouse_no_reply?: boolean;
  status?: 'default' | 'unavailable' | 'error' | 'readonly';
  can_download_candidate_data?: boolean;
};

type TextFieldProps = {
  defaultValue?: string;
  useLegacyEditor?: boolean;
} & any;

type CheckboxProps = {
  compact?: boolean;
  defaultValue?: boolean;
  helperText?: string;
  onChange?: (isChecked: boolean) => void;
  value?: string;
  label: string;
  name: string;
  rules?: Record<string, any>;
  disabled?: boolean;
};

export const ModalBodyWrapper = styled.div`
  padding-top: 4px;
  padding-bottom: 20px;
`;

export const StackWithHiddenComponents = styled(Stack)`
  .MuiGrid-item:empty {
    display: none;
  }
`;

export const RowWithHiddenComponents = styled(CompactRow)`
  .MuiGrid-item:empty {
    display: none;
  }
`;

const FormFieldWithFontFamily = styled(FormField)`
  & {
    font-family: 'Untitled Sans', sans-serif;
  }
  [role='alert'] {
    font-size: 0.875rem;
    line-height: 1.25rem;
    margin-left: 16px;
    margin-top: 4px;
  }
`;

const FormFieldWithNoMarginBottom = styled(FormField)`
  margin-bottom: 0;
`;

const HelperText = styled(SecondaryBody)`
  color: ${FullPalette.EVERGREEN_600};
`;

export const RichTextField = (props: TextFieldProps) => {
  if (props.useLegacyEditor) {
    return <LegacyRichText {...props} />;
  } else {
    return <BodyField {...props} />;
  }
};

const LegacyRichText = (props: TextFieldProps) => {
  const {
    defaultValue = '',
    name = 'body',
    label,
    rules,
    helperText,
    compact = false,
    isAiEnabled = false,
    showSchedulingLinksMenu = false,
    schedulingLinks = [],
    ...otherProps
  } = props;
  const {
    formState: { errors },
  } = useFormContext();

  const currentError = get(errors, name);
  return (
    <>
      <FormFieldWithFontFamily
        compact={compact}
        error={currentError?.message}
        data-provides={`${name}-field`}
      >
        {label && <FormLabel>{label}</FormLabel>}
        <Controller
          name={name}
          rules={rules}
          render={({ field }) => {
            const onChange = (content: string) => {
              field.onChange(content);
            };

            const emailEditorConfig = isAiEnabled
              ? {
                  ...TinyMCEConfig.AiEmail,
                  content_style: TinyMCEConfig.Email.content_style,
                  toolbar:
                    'bold italic underline fontsize | alignleft aligncenter alignright | bullist numlist | outdent indent | hr | link image code | ai_assistant',
                  relative_urls: false,
                  selector: undefined,
                  ui_mode: 'split',
                }
              : {
                  ...TinyMCEConfig.Email,
                  relative_urls: false,
                  selector: undefined,
                  ui_mode: 'split',
                };

            return (
              <TinyMCE
                id={props.id ?? `${name}_tinymce`}
                init={emailEditorConfig}
                value={field.value}
                initialValue={field.value}
                onEditorChange={onChange}
                aiProduct={AI_PRODUCT.CANDIDATE_PROFILE_EMAIL}
                showSchedulingLinksMenu={showSchedulingLinksMenu}
                schedulingLinks={schedulingLinks}
                {...otherProps}
              />
            );
          }}
        />
      </FormFieldWithFontFamily>
      {!!helperText && (
        <div style={{ marginTop: '4px', marginLeft: '16px' }}>
          <HelperText>{helperText}</HelperText>
        </div>
      )}
    </>
  );
};

const BodyField = (props: TextFieldProps) => {
  const {
    defaultValue = '',
    name = 'body',
    label,
    rules,
    helperText,
    ...otherProps
  } = props;
  const {
    formState: { errors },
    watch,
  } = useFormContext();

  // React Trix doesn't respond to prop changes so we have to
  // update the value programatically when we need to reset
  const bodyValueFromTemplate = watch(`${name}_from_template`);
  useEffect(() => {
    if (bodyValueFromTemplate) {
      const element = document.querySelector('trix-editor') as Element & {
        editor: any;
      };
      const editor = element?.editor;

      editor.setSelectedRange([0, editor.getDocument().toString().length]);
      editor.deleteInDirection('backward');
      editor.insertHTML(bodyValueFromTemplate);
    }
  }, [bodyValueFromTemplate]);

  const currentError = get(errors, name);

  return (
    <FormFieldWithFontFamily error={currentError?.message}>
      {label && <FormLabel>{label}</FormLabel>}
      <Controller
        name={name}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field }) => (
          <RichText hideAttachments={true} {...field} {...otherProps} />
        )}
      />
      {!!helperText && (
        <div style={{ marginTop: '4px', marginLeft: '16px' }}>
          <HelperText>{helperText}</HelperText>
        </div>
      )}
    </FormFieldWithFontFamily>
  );
};

type CalendarFieldProps = {
  value: Array<CalendarEvent>;
  timeZoneLabel: string | null | undefined;
  timezone?: string;
  mode: CalendarEditMode;
  handleSetApi?: (api: CalendarApi) => void;
  handleUserCreatedEventsChange?: (events: Array<CalendarEvent>) => void;
  handleRemoveEvents?: (eventId: number) => void;
  onDateChange?: (e: DatesSetArg) => void;
  timeFormat?: UserTimeFormat;
  dateFormat?: UserDateFormat;
};

export const CalendarField = (props: CalendarFieldProps) => {
  const { timezone, timeZoneLabel, ...otherProps } = props;
  return (
    <Controller
      name="events"
      render={({ field: { onChange } }) => (
        <AvailabilityCalendar
          onChange={(e) => onChange(e)}
          {...otherProps}
          timezone={timezone}
          timeZoneLabel={timeZoneLabel ?? ''}
        />
      )}
    />
  );
};

export const AttachmentRow = ({
  fileName,
  canDelete,
  canDownload,
  url,
  handleOnDelete,
}: {
  fileName: string;
  canDelete: boolean;
  canDownload?: boolean;
  url?: string;
  handleOnDelete?: () => void;
}) => (
  <AttachmentWrapper>
    <Body className="pii-blur">
      <InlineIcons>
        <AttachmentIcon />
      </InlineIcons>
      {canDownload && url ? (
        <Link href={url} target="_blank">
          {fileName}
        </Link>
      ) : (
        fileName
      )}
    </Body>

    {canDelete && handleOnDelete && (
      <IconButton
        label="Remove attachment"
        icon={CloseIcon}
        size={'sm'}
        onClick={handleOnDelete}
      />
    )}
  </AttachmentWrapper>
);

type CalendarFileType = {
  id: string;
  summary: string;
  video_conferencing_provider: VideoConferencingProviderType | null;
  time: string;
  join_url: string | null;
  handleOnDelete: (id: string) => void;
  handleOnEdit: (id: string, name: string) => void;
  setEditing: Dispatch<SetStateAction<boolean>>;
};

const EditCalendarFileRow = ({
  id,
  summary,
  setEditing,
  handleOnEdit,
}: CalendarFileType) => {
  const [name, setName] = useState(summary);

  const onSave = () => {
    handleOnEdit(id, name);
    setEditing(false);
  };

  return (
    <Grid container>
      <Grid item md={6}>
        <TextInput
          id="edit-interview-name"
          ariaLabel="interview name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </Grid>
      <Grid item md={5}>
        <GridItemWrapper>
          <SaveButton type="submit" onClick={onSave}>
            {t('save')}
          </SaveButton>
          <TertiaryButton
            size="md"
            onClick={() => {
              setEditing(false);
            }}
          >
            {t('cancel')}
          </TertiaryButton>
        </GridItemWrapper>
      </Grid>
    </Grid>
  );
};

const getVideoProviderIcon = (provider: string) => {
  switch (provider) {
    case 'meet':
      return GoogleMeetIcon;
    case 'zoom':
      return ZoomIcon;
    case 'teams':
      return MSTeamsIcon;
    default:
      return null;
  }
};

const VideoConferencingProvider = ({
  video_conferencing_provider,
  join_url,
}: {
  video_conferencing_provider: VideoConferencingProviderType | null;
  join_url: string | null;
}) => {
  if (!video_conferencing_provider) {
    if (!join_url) {
      return null;
    }
    return (
      <InlineIcons>
        <IconWrapper>
          {/* @ts-expect-error - width */}
          <VideoCallIcon width={20} />
        </IconWrapper>
        <SecondaryBody>{t('virtual_meeting')}</SecondaryBody>
      </InlineIcons>
    );
  }

  return (
    <InlineIcons>
      <ProviderLogo
        width={20}
        src={getVideoProviderIcon(video_conferencing_provider.key)}
      />
      <SecondaryBody>
        {availability(
          `video_conferencing_badge.${video_conferencing_provider.key}`
        )}
      </SecondaryBody>
    </InlineIcons>
  );
};

const DisplayCalendarFileRow = ({
  id,
  summary,
  video_conferencing_provider,
  time,
  join_url,
  setEditing,
  handleOnDelete,
}: CalendarFileType) => {
  return (
    <Grid container>
      <Grid item md={6}>
        <GridItemWrapper>
          <div
            data-provides="display-calendar-file-summary"
            data-provides-for={id}
          >
            <Body>{summary}</Body>
            <VideoConferencingProvider
              join_url={join_url}
              video_conferencing_provider={video_conferencing_provider}
            />
          </div>
        </GridItemWrapper>
      </Grid>
      <Grid item md={5}>
        <GridItemWrapper>
          <Body>{time}</Body>
        </GridItemWrapper>
      </Grid>
      <Grid item md={1}>
        <GridItemWrapper>
          <TransparentMenuButton>
            <IconButton
              icon={EllipsisIcon}
              onClick={() => {
                return;
              }}
              label="actions"
              size="md"
            />
          </TransparentMenuButton>
          <MenuList portal={true}>
            <MenuItem
              onSelect={() => setEditing((currentState) => !currentState)}
            >
              {t('rename')}
            </MenuItem>
            <MenuItem
              onSelect={() => {
                handleOnDelete(id);
              }}
            >
              {t('remove')}
            </MenuItem>
          </MenuList>
        </GridItemWrapper>
      </Grid>
    </Grid>
  );
};

export const CalendarFilesField = () => {
  const { setValue, watch, getValues } = useFormContext();

  const calendar_files = watch('calendar_files');

  const removeCalendarFile = (id: string) => {
    const removed = calendar_files.filter((file: any) => file.id !== id);
    setValue('calendar_files', removed);
    if (removed.length === 0) {
      setValue('include_calendar_files', false);
    }
  };

  const editCalendarFile = (id: string, summary: string) => {
    //returns a copy
    const batches = getValues('calendar_files');
    const foundFile = batches.find((file: any) => file.id === id);
    foundFile.summary = summary;
    setValue('calendar_files', batches);
  };

  return calendar_files.map((interview: any) => {
    return (
      <CalendarFileRow
        key={interview.id}
        canDelete={true}
        handleOnDelete={removeCalendarFile}
        handleOnEdit={editCalendarFile}
        {...interview}
      />
    );
  });
};

const CalendarFileRow = (props: CalendarFileType) => {
  const [editing, setEditing] = useState(false);
  return (
    <ExpandableMenu>
      <AttachmentWrapper>
        {editing ? (
          <EditCalendarFileRow {...props} setEditing={setEditing} />
        ) : (
          <DisplayCalendarFileRow {...props} setEditing={setEditing} />
        )}
      </AttachmentWrapper>
    </ExpandableMenu>
  );
};

const DefaultAttachments = ({
  attachments,
  handleOnDelete,
  canDownload,
}: {
  attachments: TemplateAttachment[];
  handleOnDelete: (filename: string) => void;
  canDownload?: boolean;
}) => (
  <>
    {attachments.map(({ filename, url }, index) => (
      <AttachmentRow
        fileName={filename}
        canDelete={true}
        canDownload={canDownload}
        url={url}
        key={index}
        handleOnDelete={() => handleOnDelete(filename)}
      />
    ))}
  </>
);

const NewAttachments = ({
  attachments,
  handleOnDelete,
}: {
  attachments: File[];
  handleOnDelete: (filename: string) => void;
}) => {
  if (!attachments.length) {
    return null;
  }

  return (
    <>
      {Array.from(attachments).map((file, index) => (
        <AttachmentRow
          fileName={file.name}
          canDelete={true}
          key={index}
          handleOnDelete={() => handleOnDelete(file.name)}
        />
      ))}
    </>
  );
};

type AttachmentsFieldProps = {
  allowMultiple: boolean;
  canDownload?: boolean;
  accept?: string[];
  validate?: (value: FileList) => string | boolean;
  compact?: boolean;
};

export const AttachmentsField = (props: AttachmentsFieldProps) => {
  const {
    allowMultiple = true,
    canDownload = false,
    accept,
    validate,
    compact = false,
    ...otherProps
  } = props;
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useFormContext();

  const existingAttachments = watch('default_attachments');
  const currentAttachments = watch('attachments');

  const removeAttachment = (filename: string) => {
    setValue(
      'default_attachments',
      existingAttachments.filter(
        (attachment: TemplateAttachment) => attachment.filename !== filename
      )
    );

    clearErrors('attachments');
  };

  const removeNewAttachments = (filename: string) => {
    setValue(
      'attachments',
      Array.from(currentAttachments).filter(
        (file: any) => file.name !== filename // eslint-disable-line @typescript-eslint/no-explicit-any
      )
    );
  };
  return (
    <>
      <FormFieldWithNoMarginBottom
        compact={compact}
        error={errors.attachments?.message}
      >
        <Label>{t(allowMultiple ? 'attachments' : 'attachment')}</Label>

        {/* Default attachments are sent from the server as { filename, url } */}
        {Array.isArray(existingAttachments) && (
          <DefaultAttachments
            attachments={existingAttachments}
            handleOnDelete={removeAttachment}
            canDownload={canDownload}
          />
        )}

        {/* New attachments are in a FileList that comes from the file input */}
        {currentAttachments && (
          <NewAttachments
            attachments={Array.from(currentAttachments)}
            handleOnDelete={removeNewAttachments}
          />
        )}

        <StyledFileInput
          multiple={allowMultiple}
          type="file"
          {...register('default_attachments')}
          {...otherProps}
          accept={accept?.join(',')}
        />
      </FormFieldWithNoMarginBottom>
      <AttachmentLabel tabIndex={0}>
        {/* @ts-expect-error - should be a tertiary button but there's dark magic with how it opens the file input */}
        <Link>{t('attach_file')}</Link>
        <StyledFileInput
          multiple={allowMultiple}
          type="file"
          {...register('attachments', {
            validate,
          })}
          {...otherProps}
          accept={accept?.join(',')}
        />
      </AttachmentLabel>
    </>
  );
};

export const getAndFormatAttachments = (
  getValues: UseFormGetValues<FieldValues>
) => {
  const existingAttachments: FileList | undefined = getValues(
    'default_attachments'
  );
  const attachments = getValues('attachments');

  const existingAttachmentIds = Array.isArray(existingAttachments)
    ? existingAttachments.map((file) => file.id)
    : undefined;

  return {
    default_attachment_ids: existingAttachmentIds,
    custom_attachment: attachments,
  };
};

type AsyncDropdownFieldProps = {
  name: string;
  label?: string;
  getOptionsUrl: string;
  params?: any;
  rules?: any;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  preventLoadOnMount?: boolean;
  transformValues?: (data: any) => Array<DropdownOption>;
  ariaLabel?: string;
  item?: Item<string | number> | null | undefined;
  onChange?: ((arg1: Item<string>) => void) | undefined;
};
export const AsyncDropdownField = (props: AsyncDropdownFieldProps) => {
  const {
    name,
    getOptionsUrl,
    rules,
    transformValues,
    preventLoadOnMount,
    params,
    item,
    onChange,
  } = props;

  const {
    formState: { errors },
  } = useFormContext();

  const fetchItems = async (term: string) => {
    const response = await standardRequestWithErrorFlash.get(getOptionsUrl, {
      params: { ...params, term },
    });
    if (transformValues) {
      const items = transformValues(response.data);
      return { items };
    }
    return response.data;
  };
  const currentError = get(errors, name);

  return (
    <Controller
      render={({ field }) => {
        const onSelectedItemChange = (e: any) => {
          if (onChange) {
            onChange(e.selectedItem);
          } else {
            field.onChange(e.selectedItem);
          }
        };

        const selectedItem = item || (field.value ?? null);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const dropdownProps = useSingleDropdown({
          fetchItems,
          selectedItem,
          onSelectedItemChange,
          preventLoadOnMount: preventLoadOnMount ?? true,
        });

        return (
          <SingleDropdown dropdownProps={dropdownProps}>
            <SingleDropdown.Field
              {...props}
              {...field}
              error={!!currentError}
              // @ts-expect-error - TS2322: Type 'string | FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined' is not assignable to type 'string | undefined'
              helperText={currentError?.message}
              data-provides={`${name}-field`}
            />
            <SingleDropdown.Flyout data-provides={`${name}-flyout`} />
          </SingleDropdown>
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

export type PaginatedDropdownProps = {
  name: string;
  label?: string;
  ariaLabel?: string;
  getOptionsUrl: string;
  rules?: RegisterOptions;
  disabled?: boolean;
  params?: { [key: string]: string | number[] | boolean };
  placeholder?: string;
  applicationId?: number;
  preventLoadOnMount?: boolean;
  helperText?: string;
  creatable?: boolean;
  pageSize?: number;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformValues?: (data: any) => Array<DropdownOption>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  remountKey?: string;
  isPiiBlurred?: boolean;
};

export const PaginatedMultiDropdown = (props: PaginatedDropdownProps) => {
  const {
    name,
    getOptionsUrl,
    rules,
    preventLoadOnMount,
    applicationId,
    creatable,
    ariaLabel,
    params,
    transformValues,
    pageSize = 200,
    remountKey,
    isPiiBlurred,
    ...otherProps
  } = props;

  const {
    formState: { errors },
  } = useFormContext();

  const fetchItems = async (
    term: string,
    nextPage: number
  ): Promise<{ items: Item[]; more: boolean }> => {
    const response = await standardRequestWithErrorFlash.get(getOptionsUrl, {
      params: {
        term,
        page: nextPage,
        ...params,
      },
    });
    let items = response.data.results;
    if (transformValues) {
      items = transformValues(response.data);
    }
    const more = response.data.more;

    return { items, more };
  };

  const paginatedItems = fetchItems;

  const currentError = get(errors, name);

  let label: string | ReactElement = otherProps.label ?? '';

  if (rules && rules.hasOwnProperty('required') && !!rules.required) {
    label = (
      <span>
        {otherProps.label} <RequiredAsterisk>{'*'}</RequiredAsterisk>
      </span>
    );
  }

  return (
    <Controller
      key={remountKey}
      render={({ field }) => {
        const onSelectedItemsChange = (e: {
          selectedItems: Item<string>[] | null;
        }) => {
          field.onChange(e.selectedItems);
        };

        const selectedItems = field.value ?? [];

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const dropdownProps = useMultiDropdown({
          fetchItems: paginatedItems,
          paginated: true,
          selectedItems,
          onSelectedItemsChange,
          creatable,
          preventLoadOnMount: preventLoadOnMount ?? true,
          isPiiBlurred,
        });

        return (
          <MultiDropdown dropdownProps={dropdownProps}>
            <MultiDropdown.Field
              {...props}
              //@ts-expect-error - label will be a string or a ReactElement
              label={label}
              {...field}
              error={!!currentError}
              helperText={currentError?.message?.toString()}
              data-provides={`${name}-field`}
            />
            <MultiDropdown.Flyout
              disablePortal
              data-provides={`${name}-flyout`}
            />
          </MultiDropdown>
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

export const PaginatedSingleDropdown = (props: PaginatedDropdownProps) => {
  const {
    name,
    getOptionsUrl,
    rules,
    preventLoadOnMount,
    applicationId,
    creatable,
    ariaLabel,
    params,
    helperText,
    pageSize = 200,
    transformValues,
    isPiiBlurred,
    ...otherProps
  } = props;

  const {
    formState: { errors },
  } = useFormContext();

  const fetchItems = async (
    term: string,
    nextPage: number
  ): Promise<{ items: Item[]; more: boolean }> => {
    const response = await standardRequestWithErrorFlash.get(getOptionsUrl, {
      params: {
        term,
        page: nextPage,
        ...params,
      },
    });
    let items = response.data.results;
    if (transformValues) {
      items = transformValues(response.data);
    }
    const more = response.data.more;

    return { items, more };
  };

  const paginatedItems = fetchItems;

  const currentError = get(errors, name);

  let label: string | ReactElement = otherProps.label ?? '';

  if (rules && rules.hasOwnProperty('required') && !!rules.required) {
    label = (
      <span>
        {otherProps.label} <RequiredAsterisk>{'*'}</RequiredAsterisk>
      </span>
    );
  }

  return (
    <Controller
      render={({ field }) => {
        const onSelectedItemChange = (e: {
          selectedItem?: Item<string> | null;
        }) => {
          field.onChange(e.selectedItem);
        };

        const selectedItem = field.value;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const dropdownProps = useSingleDropdown({
          fetchItems: paginatedItems,
          paginated: true,
          selectedItem,
          onSelectedItemChange,
          preventLoadOnMount: preventLoadOnMount ?? true,
          isPiiBlurred,
        });

        return (
          <SingleDropdown dropdownProps={dropdownProps}>
            <SingleDropdown.Field
              {...props}
              //@ts-expect-error - label will be a string or a ReactElement
              label={label}
              {...field}
              error={!!currentError}
              helperText={helperText ?? currentError?.message?.toString()}
              data-provides={`${name}-field`}
            />
            <SingleDropdown.Flyout
              disablePortal
              data-provides={`${name}-flyout`}
            />
          </SingleDropdown>
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

type TextAreaFieldProps = {
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  style?: any;
  compact?: boolean;
  rules?: UseControllerProps['rules'];
  disabled?: boolean;
  helperText?: string;
  className?: string;
};

const Parent = ({
  children,
  compact,
}: {
  children: ReactNode;
  compact: boolean;
}) => (compact ? <>{children}</> : <FormField>{children}</FormField>);

export const TextAreaField = (props: TextAreaFieldProps) => {
  const {
    name,
    label,
    placeholder,
    defaultValue = '',
    compact = false,
    rules,
    helperText,
    ...otherProps
  } = props;
  const {
    formState: { errors },
  } = useFormContext();

  const currentError = get(errors, name);

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      render={({ field }) => (
        <Parent compact={compact}>
          <TextArea
            label={label}
            ariaLabel={name}
            autoResize={true}
            placeholder={placeholder}
            {...field}
            {...otherProps}
            data-provides={`${name}-field`}
            error={!!currentError}
            helperText={currentError?.message?.toString() || helperText}
          />
        </Parent>
      )}
      rules={rules}
    />
  );
};

type SimpleInputTextFieldProps = {
  name: string;
  id: string;
  rules?: any;
} & any;

export const SimpleFormTextField = ({
  name,
  rules,
  id,
  inputClassName,
  ...props
}: SimpleInputTextFieldProps & { inputClassName?: string }) => {
  const {
    formState: { errors },
  } = useFormContext();

  const currentError = get(errors, name);

  if (
    rules &&
    rules.hasOwnProperty('required') &&
    !!rules.required &&
    !props.required
  ) {
    props.label = (
      <span>
        {props.label} <RequiredAsterisk>*</RequiredAsterisk>
      </span>
    );
  }

  return (
    <Controller
      render={({ field }) => {
        return (
          <BirchTextInput
            id={id}
            data-provides={`${name}-input`}
            className={inputClassName}
            {...props}
            {...field}
            error={!!currentError}
            helperText={currentError?.message || props.helperText}
          />
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

export const DateField = ({
  id,
  name,
  rules,
  ...props
}: SimpleInputTextFieldProps) => {
  const {
    formState: { errors },
  } = useFormContext();
  if (rules && rules.hasOwnProperty('required') && !!rules.required) {
    props.label = (
      <span>
        {props.label} <RequiredAsterisk>*</RequiredAsterisk>
      </span>
    );
  }

  const currentError = get(errors, name);
  return (
    <Controller
      render={({ field }) => {
        return (
          <TextInput
            id={id}
            type="date"
            {...props}
            {...field}
            error={!!currentError}
            helperText={currentError?.message?.toString()}
            data-provides={`${name}-field`}
          />
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

type SimpleTextAreaProps = {
  name: string;
  id: string;
  rules?: any;
  rows?: number | 'auto';
} & any;

export const SimpleTextAreaField = ({
  name,
  rules,
  id,
  rows,
  ...props
}: SimpleTextAreaProps) => {
  const {
    formState: { errors },
  } = useFormContext();

  const currentError = get(errors, name);

  return (
    <Controller
      render={({ field }) => {
        return (
          <BirchTextArea
            id={id}
            {...props}
            {...field}
            rows={rows}
            error={!!currentError}
            helperText={currentError?.message || props.helperText}
            data-provides={`${name}-field`}
          />
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

type DropdownProps<T> = {
  name: string;
  items:
    | Array<Item<T>>
    | ((text: string) => Promise<{ items: Array<Item<T>> }>);
  rules?: UseControllerProps['rules'];
  creatable?: boolean;
  onSelectedItemChange?: (value: any) => void | Promise<void>;
  preventLoadOnMount?: boolean;
  isPiiBlurred?: boolean;
} & Record<string, any>;

export const SimpleFormSingleDropdown = <T = string,>({
  name,
  items,
  rules,
  onSelectedItemChange: propsOnSelectedItemChange,
  defaultValue,
  helperText,
  preventLoadOnMount,
  isPiiBlurred,
  ...props
}: DropdownProps<T>) => {
  const {
    formState: { errors },
  } = useFormContext();

  const currentError = get(errors, name);

  if (
    rules &&
    rules.hasOwnProperty('required') &&
    !!rules.required &&
    !props.required &&
    !!props.label
  ) {
    props.label = (
      <span>
        {props.label} <RequiredAsterisk>*</RequiredAsterisk>
      </span>
    );
  }

  return (
    <Controller
      render={({ field }) => {
        const onSelectedItemChange = async (e: any) => {
          if (propsOnSelectedItemChange) {
            propsOnSelectedItemChange?.(e.selectedItem);
          }

          field.onChange(e.selectedItem);
        };

        const selectedItem = field?.value;

        const itemsProps =
          typeof items === 'function'
            ? {
                fetchItems: items,
                preventLoadOnMount: preventLoadOnMount ?? true,
              }
            : {
                items,
              };

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const dropdownProps = useSingleDropdown<T>({
          selectedItem,
          initialSelectedItem: defaultValue,
          onSelectedItemChange,
          isPiiBlurred,
          ...itemsProps,
        });

        return (
          <SingleDropdown<T> dropdownProps={dropdownProps}>
            {/* @ts-expect-error - label prop */}
            <SingleDropdown.Field
              {...props}
              {...field}
              error={!!currentError}
              helperText={currentError?.message || helperText}
              data-provides={name + '-field'}
            />
            <SingleDropdown.Flyout
              disablePortal
              data-provides={name + '-flyout'}
            />
          </SingleDropdown>
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

export const SimpleFormMultiDropdown = <T = string,>({
  name,
  items,
  rules,
  creatable,
  preventLoadOnMount,
  isPiiBlurred,
  ...props
}: DropdownProps<T>) => {
  const {
    formState: { errors },
  } = useFormContext();

  const currentError = get(errors, name);

  if (rules && rules.hasOwnProperty('required') && !!rules.required) {
    props.label = (
      <span>
        {props.label} <RequiredAsterisk>*</RequiredAsterisk>
      </span>
    );
  }

  return (
    <Controller
      render={({ field }) => {
        const onSelectedItemsChange = (e: {
          selectedItems: Array<Item<T>>;
        }) => {
          field.onChange(e.selectedItems);
        };

        const selectedItems = field.value ?? [];

        const itemsProps =
          typeof items === 'function'
            ? {
                fetchItems: items,
                preventLoadOnMount: preventLoadOnMount ?? true,
              }
            : {
                items,
              };

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const dropdownProps = useMultiDropdown<T>({
          selectedItems,
          creatable,
          onSelectedItemsChange,
          isPiiBlurred,
          ...itemsProps,
        });

        return (
          <MultiDropdown<T> dropdownProps={dropdownProps}>
            <MultiDropdown.Field
              ariaLabel={props.label}
              {...props}
              {...field}
              error={!!currentError}
              helperText={currentError?.message?.toString()}
              data-provides={`${name}-field`}
            />
            <MultiDropdown.Flyout data-provides={`${name}-flyout`} />
          </MultiDropdown>
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

export const CheckboxField = ({
  name,
  label,
  compact,
  disabled = false,
  helperText,
  onChange,
  ...fieldProps
}: CheckboxProps) => {
  const {
    formState: { errors },
  } = useFormContext();

  const currentError = get(errors, name);

  const Parent = ({ children }: { children: ReactNode }) =>
    compact ? <>{children}</> : <FormField>{children}</FormField>;

  return (
    <Controller
      render={({ field }) => {
        return (
          <Parent>
            <Checkbox
              {...field}
              label={label}
              checked={field.value}
              helperText={helperText || currentError?.message?.toString()}
              onChange={(isChecked) => {
                if (typeof onChange === 'function') {
                  onChange(isChecked);
                }
                // this allows for both binary values (field=true)
                // and non-binary values (field="some value") if value is supplied
                const value = isChecked ? fieldProps.value ?? true : false;
                field.onChange(value);
              }}
              error={!!currentError?.message}
              disabled={disabled}
              data-provides={`${name}-checkbox`}
            />
          </Parent>
        );
      }}
      name={name}
      {...fieldProps}
    />
  );
};

export const HiddenField = ({ name }: { name: string }) => {
  return (
    <Controller
      name={name}
      render={({ field }) => <input type="hidden" {...field} />}
    />
  );
};

type WithAria = {
  ariaLabelledBy: string;
  label?: string;
};

type WithLabel = {
  label: string;
  arialLabelledBy?: string;
};

type DatePickerFieldProps = {
  name: string;
  id?: string;
  dateFormat: string;
  minDate?: Date;
  rules?: UseControllerProps['rules'];
} & (WithAria | WithLabel) &
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, any>;

export const DatePickerField = ({
  name,
  rules,
  ...props
}: DatePickerFieldProps) => {
  const {
    formState: { errors },
  } = useFormContext();
  const currentError = get(errors, name);
  return (
    <Controller
      render={({ field }) => {
        return (
          <div data-provides={`${name}-field`}>
            <DatePicker
              {...props}
              {...field}
              onChange={(date) =>
                // force UTC noon of the selected date
                field.onChange(
                  date
                    ? new Date(
                        Date.UTC(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate(),
                          12
                        )
                      )
                    : date
                )
              }
              helperText={currentError?.message?.toString()}
              selected={field.value}
              error={!!currentError}
            />
          </div>
        );
      }}
      name={name}
      rules={rules}
    />
  );
};

const StyledFileInput = styled.input`
  display: none;
`;

const AttachmentLabel = styled(FormLabel)`
  display: inline-block;
`;

const AttachmentWrapper = styled.div`
  border-bottom: 1px solid ${FullPalette.GREY_E500};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  span {
    margin-right: 10px;
  }
  &:hover {
    background-color: ${FullPalette.IRIS_E25};
  }
`;

const ProviderLogo = styled.img`
  margin-right: 8px;
`;

const IconWrapper = styled.div`
  margin-right: 8px;
`;

const GridItemWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const SaveButton = styled(PrimaryButton)`
  margin-right: 16px;
`;

export const TertiaryButtonNoPadding = styled(TertiaryButton)`
  padding: 0;
`;

export const URL_REGEX =
  /^(http:\/\/|https:\/\/)?[0-9a-z\-]+\.[0-9a-z.\-]+(\/.*)?$/i;
